import { css } from '@emotion/react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { InfoBox } from '../../Mappings/forms/InfoBox';
import { fetchOptionUsage } from '../../../services/queries/MaggieCustomFieldQueries';
import { CustomFieldFormType } from './CustomField.schema';

const containerCss = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export function RemovedOptionsWarning() {
  const { control, setValue } = useFormContext<CustomFieldFormType>();
  const fieldId = useWatch({ name: 'id', control });
  const currentOptions = useWatch({ name: '_viewModel.selectConfigValues', control });
  const cleanupChecked = useWatch({ name: '_viewModel.cleanupRemovedOptions', control });
  const [usageCounts, setUsageCounts] = useState<Record<string, number> | null>(null);

  const initialOptionsRef = useRef<string[]>(currentOptions?.filter((o): o is string => !!o) ?? []);

  useEffect(() => {
    const initial = initialOptionsRef.current;
    const currentSet = new Set(currentOptions ?? []);
    const removedOptions = initial.filter((v) => !currentSet.has(v));

    if (!fieldId || removedOptions.length === 0) {
      setUsageCounts(null);
      return;
    }

    let cancelled = false;
    fetchOptionUsage(fieldId, removedOptions)
      .then((counts) => {
        if (!cancelled) {
          setUsageCounts(counts);
        }
      })
      .catch((err) => {
        console.error('[RemovedOptionsWarning] fetchOptionUsage failed', err);
        if (!cancelled) {
          setUsageCounts(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [fieldId, currentOptions]);

  const optionsInUse = usageCounts ? Object.entries(usageCounts).filter(([, count]) => count > 0) : [];

  useEffect(() => {
    if (optionsInUse.length === 0) {
      setValue('_viewModel.cleanupRemovedOptions', true);
    }
  }, [optionsInUse.length, setValue]);

  const handleCheckboxChange = useCallback(
    (_: unknown, checked: boolean) => {
      setValue('_viewModel.cleanupRemovedOptions', checked);
    },
    [setValue]
  );

  if (optionsInUse.length === 0) {
    return null;
  }

  const message = optionsInUse
    .map(([name, count]) => `"${name}" is used by ${count} ${count === 1 ? 'record' : 'records'}`)
    .join(', ');

  return (
    <div css={containerCss}>
      <InfoBox
        variant='warning'
        message={
          <Typography variant='body2' component='span'>
            {message}. Removing {optionsInUse.length === 1 ? 'this option' : 'these options'} may leave stale
            data on existing records.
          </Typography>
        }
      />
      <FormControlLabel
        control={<Checkbox checked={cleanupChecked ?? true} onChange={handleCheckboxChange} size='small' />}
        label={
          <Typography variant='body2'>
            Remove {optionsInUse.length === 1 ? 'this value' : 'these values'} from all existing records
          </Typography>
        }
      />
    </div>
  );
}
